import React from 'react';
import { Platform, ScrollView, View } from 'react-native';
import Text from '@/components/Typography/Text';
import { safeAccessibilityRole } from '@/constants/utils/a11y';
import MetaHead from '@/components/MetaHead';
import PublicFooter from '@/components/PublicFooter';
import { Section, useLanding } from '@/components/Landing/LandingKit';
import useLandingStyles from '@/components/Landing/useLandingStyles';
import BusinessSeatCalculator from '@/components/Business/BusinessSeatCalculator';
import BusinessCta from '@/components/Business/BusinessCta';
import PageVideo from '@/components/Tutorials/PageVideo';
import useBusinessBrand from '@/components/Business/businessBrand';
import useBusinessEnrollment from '@/components/Business/useBusinessEnrollment';
import { businessCopy } from '@/constants/businessContent';

export default function BusinessPage() {
  const { isCompact, width } = useLanding();
  const c = useBusinessBrand();
  const copy = businessCopy(useBusinessEnrollment());
  useLandingStyles();
  if (Platform.OS !== 'web') return null;
  return (
    <View style={{ flex: 1, backgroundColor: c.page }}>
      <MetaHead title="Gab AI Business" description={copy.description} canonical="/business" keywords="Gab AI Business, team AI, company billing, shared AI credits, Plus Premium seats" />
      <ScrollView dataSet={{ landingScroll: 'true' }} accessibilityRole={safeAccessibilityRole('main')} nativeID="main-content" contentContainerStyle={{ alignItems: 'center' }}>
        <Section label="Gab AI Business" contentStyle={{ paddingTop: isCompact ? 120 : 154 }}>
          <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: isCompact ? 36 : 64, alignItems: 'flex-start' }}>
            <View style={{ flex: 1, gap: 24, minWidth: 0 }}>
              <Text weight="medium" style={{ color: c.muted, fontSize: 15 }}>{copy.availability ? `Gab AI Business · ${copy.availability}` : 'Gab AI Business'}</Text>
              <Text tag="h1" weight="semibold" style={{ color: c.ink, fontSize: Math.min(60, width / 9), lineHeight: Math.min(66, width / 8), letterSpacing: -1.6 }}>AI for your team.{ '\n' }One company bill.</Text>
              <Text tag="p" style={{ color: c.muted, fontSize: 18, lineHeight: 29 }}>Bring your people together with Plus and Plus Premium seats, shared credits, and a clear view of company spending.</Text>
              <Text style={{ color: c.muted, fontSize: 15, lineHeight: 25 }}>{copy.intro}</Text>
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 12 }}>
                {copy.primary && <BusinessCta label={copy.primary.label} href={copy.primary.href} />}
                <BusinessCta label="Read the Business guide" href="/docs/business" secondary />
              </View>
            </View>
            <View style={{ flex: 1, minWidth: 0, width: '100%' }}><BusinessSeatCalculator /></View>
          </View>
          <View style={{ marginTop: isCompact ? 40 : 64 }}>
            <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 30, marginBottom: 30 }}>See Gab AI at work.</Text>
            <PageVideo page="business" />
          </View>
        </Section>
        <Section label="What your team can hand to Gab AI" style={{ borderTopWidth: 1, borderTopColor: c.rule }}>
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 30, marginBottom: 14 }}>Hand it real work.</Text>
          <Text tag="p" style={{ color: c.muted, fontSize: 16, lineHeight: 26, marginBottom: 26 }}>Chat when you're thinking. Work when you're doing. A task keeps its files, its steps, and its history — nothing dead-ends in a chat thread.</Text>
          <View style={{ gap: 36 }}>
            <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 36 }}>
              <View style={{ flex: 1, gap: 12 }}>
                <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 21 }}>Research &amp; reading</Text>
                <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>Deep research, reading lists, and briefs, with sources to check, not confident guesses.</Text>
              </View>
              <View style={{ flex: 1, gap: 12 }}>
                <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 21 }}>Decks, docs &amp; spreadsheets</Text>
                <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>Presentations, reports, and working spreadsheets, made in Work and left yours to edit.</Text>
              </View>
            </View>
            <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 36 }}>
              <View style={{ flex: 1, gap: 12 }}>
                <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 21 }}>Automations &amp; scripts</Text>
                <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>Recurring tasks and small automations: gather data, update a sheet, keep the files tidy.</Text>
              </View>
              <View style={{ flex: 1, gap: 12 }}>
                <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 21 }}>Code &amp; prototypes</Text>
                <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>Working prototypes, small tools, and code that runs, built beside your people.</Text>
              </View>
            </View>
          </View>
        </Section>
        <Section label={copy.featuresLabel} style={{ borderTopWidth: 1, borderTopColor: c.rule }}>
          <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 30, marginBottom: 30 }}>Built around your team</Text>
          <View style={{ flexDirection: isCompact ? 'column' : 'row', gap: 36 }}>
            {copy.features.map(([title, body]) => <View key={title} style={{ flex: 1, gap: 12 }}>
              <Text tag="h3" weight="semibold" style={{ color: c.ink, fontSize: 21 }}>{title}</Text>
              <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>{body}</Text>
            </View>)}
          </View>
        </Section>
        <Section label="Existing Gab AI accounts" style={{ borderTopWidth: 1, borderTopColor: c.rule }}>
          <View style={{ maxWidth: 760, gap: 20 }}>
            <Text tag="h2" weight="semibold" style={{ color: c.ink, fontSize: 30 }}>Keep your account. Add your business.</Text>
            <Text style={{ color: c.muted, fontSize: 17, lineHeight: 28 }}>{copy.storeNotice}</Text>
            <Text style={{ color: c.muted, fontSize: 16, lineHeight: 26 }}>{copy.existingDetail}</Text>
            <BusinessCta label="Existing subscriber guide" href="/docs/business-upgrade" secondary />
          </View>
        </Section>
        <PublicFooter variant="editorial" showHero={false} />
      </ScrollView>
    </View>
  );
}
